import React, { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useProcurement } from "@/context/ProcurementContext";
import { VendorList, VendorForm, VendorDetails, VendorInvoicesModal } from "@/components/vendor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Plus, 
  Search, 
  Filter, 
  Download,
  Upload,
  Building2,
  Users,
  TrendingUp,
  Star,
  Clock,
  CheckCircle,
  RefreshCw
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const VendorManagement = () => {
  const { state, actions } = useProcurement();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState<number | null>(null);
  const [viewingVendor, setViewingVendor] = useState<number | null>(null);
  const [viewingInvoicesForVendor, setViewingInvoicesForVendor] = useState<number | null>(null);

  // Load vendors on component mount
  useEffect(() => {
    actions.fetchVendors();
  }, []);

  // Debug: Log vendor data when it changes
  useEffect(() => {
    console.log('VendorManagement - Current vendors:', state.vendors.length);
    console.log('VendorManagement - Vendor data:', state.vendors);
  }, [state.vendors]);

  // Filter vendors based on search and filters
  const filteredVendors = state.vendors.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.vendorCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.contactDetails.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || 
                         (statusFilter === "active" && vendor.isActive) ||
                         (statusFilter === "inactive" && !vendor.isActive);
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== "all") {
      const vendorDate = new Date(vendor.createdAt);
      const today = new Date();
      const daysDiff = Math.floor((today.getTime() - vendorDate.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (dateFilter) {
        case "today":
          matchesDate = daysDiff === 0;
          break;
        case "week":
          matchesDate = daysDiff <= 7;
          break;
        case "month":
          matchesDate = daysDiff <= 30;
          break;
        case "quarter":
          matchesDate = daysDiff <= 90;
          break;
        case "year":
          matchesDate = daysDiff <= 365;
          break;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Handle vendor creation/update
  const handleSaveVendor = async (vendorData: any): Promise<boolean> => {
    const success = editingVendor 
      ? await actions.updateVendor(editingVendor, vendorData)
      : await actions.createVendor(vendorData);
    
    if (success) {
      setShowForm(false);
      setEditingVendor(null);
      actions.fetchVendors(); // Refresh the list
    }
    
    return success;
  };

  // Handle vendor deletion
  const handleDeleteVendor = async (id: number) => {
    const success = await actions.deleteVendor(id);
    if (success) {
      actions.fetchVendors(); // Refresh the list
    }
  };

  // Handle bulk operations
  const handleBulkExport = () => {
    const csvContent = [
      ['Vendor Code', 'Name', 'Email', 'Phone', 'Performance Rating', 'On-Time Delivery', 'Status'],
      ...filteredVendors.map(vendor => [
        vendor.vendorCode,
        vendor.name,
        vendor.contactDetails.email,
        vendor.contactDetails.phone,
        vendor.performanceRating.toString(),
        vendor.deliveryMetrics.totalOrders > 0 
          ? Math.round((vendor.deliveryMetrics.onTimeDeliveries / vendor.deliveryMetrics.totalOrders) * 100).toString() + '%'
          : '0%',
        vendor.isActive ? 'Active' : 'Inactive'
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendors-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Vendors exported successfully');
  };

  const handleBulkImport = () => {
    // Simulate bulk import
    toast.info('Bulk import feature coming soon');
  };

  const handleRefreshData = async () => {
    try {
      await actions.refreshMockData();
      toast.success("Vendor data refreshed to original state");
    } catch (error) {
      toast.error("Failed to refresh vendor data");
    }
  };

  // Expose refresh function to window for debugging
  React.useEffect(() => {
    (window as any).refreshVendorData = handleRefreshData;
    return () => {
      delete (window as any).refreshVendorData;
    };
  }, []);

  // Calculate statistics
  const stats = {
    total: state.vendors.length,
    active: state.vendors.filter(vendor => vendor.isActive).length,
    excellent: state.vendors.filter(vendor => vendor.performanceRating >= 4.5).length,
    totalOrders: state.vendors.reduce((sum, vendor) => sum + vendor.deliveryMetrics.totalOrders, 0),
    averageRating: state.vendors.length > 0 
      ? state.vendors.reduce((sum, vendor) => sum + vendor.performanceRating, 0) / state.vendors.length 
      : 0,
    onTimePercentage: (() => {
      const totalOrders = state.vendors.reduce((sum, vendor) => sum + vendor.deliveryMetrics.totalOrders, 0);
      const onTimeDeliveries = state.vendors.reduce((sum, vendor) => sum + vendor.deliveryMetrics.onTimeDeliveries, 0);
      return totalOrders > 0 ? Math.round((onTimeDeliveries / totalOrders) * 100) : 0;
    })()
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Vendor Management</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Manage your supplier network and track performance</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleBulkExport} className="flex-1 sm:flex-none">
              <Download className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Export</span>
              <span className="xs:hidden">Export</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleBulkImport} className="flex-1 sm:flex-none">
              <Upload className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Import</span>
              <span className="xs:hidden">Import</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={async () => {
                try {
                  await actions.refreshMockData();
                  await actions.fetchVendors();
                  toast.success("Vendor data reset to mock data");
                } catch (error) {
                  toast.error("Failed to reset vendor data");
                }
              }}
              className="flex-1 sm:flex-none"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Reset to Mock Data</span>
              <span className="xs:hidden">Reset</span>
            </Button>
            <Button onClick={() => setShowForm(true)} className="flex-1 sm:flex-none">
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Add Vendor</span>
              <span className="xs:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
                <div className="ml-3 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">Total Vendors</p>
                  <p className="text-lg sm:text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">
                    {stats.active} active
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Star className="h-8 w-8 text-yellow-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-muted-foreground">Avg Rating</p>
                  <p className="text-2xl font-bold">{stats.averageRating.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground">
                    {stats.excellent} excellent (4.5+)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-muted-foreground">On-Time Delivery</p>
                  <p className="text-2xl font-bold">{stats.onTimePercentage}%</p>
                  <p className="text-xs text-muted-foreground">
                    Across all vendors
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-muted-foreground">Total Orders</p>
                  <p className="text-2xl font-bold">{stats.totalOrders}</p>
                  <p className="text-xs text-muted-foreground">
                    All time
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="space-y-4">
            {/* Filters and Search */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filters & Search
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search vendors by name, code, or email..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={dateFilter} onValueChange={setDateFilter}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Date" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Dates</SelectItem>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="week">Last 7 days</SelectItem>
                        <SelectItem value="month">Last 30 days</SelectItem>
                        <SelectItem value="quarter">Last 90 days</SelectItem>
                        <SelectItem value="year">Last year</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSearchTerm("");
                        setStatusFilter("all");
                        setDateFilter("all");
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vendor List */}
            <VendorList
              vendors={filteredVendors}
              onEdit={(id) => {
                setEditingVendor(id);
                setShowForm(true);
              }}
              onView={(id) => setViewingVendor(id)}
              onDelete={handleDeleteVendor}
              onViewInvoices={(id) => {
                setViewingInvoicesForVendor(id);
              }}
              isLoading={state.isLoading}
            />
        </div>

        {/* Vendor Form Modal */}
        {showForm && (
          <VendorForm
            vendor={editingVendor ? state.vendors.find(v => v.id === editingVendor) : undefined}
            onSave={handleSaveVendor}
            onCancel={() => {
              setShowForm(false);
              setEditingVendor(null);
            }}
          />
        )}

        {/* Vendor Details Modal */}
        {viewingVendor && (
          <VendorDetails
            vendorId={viewingVendor}
            onClose={() => setViewingVendor(null)}
            onEdit={(id) => {
              setViewingVendor(null);
              setEditingVendor(id);
              setShowForm(true);
            }}
          />
        )}

        {/* Vendor Invoices Modal */}
        {viewingInvoicesForVendor && (
          <VendorInvoicesModal
            vendorId={viewingInvoicesForVendor}
            vendorName={state.vendors.find(v => v.id === viewingInvoicesForVendor)?.name}
            isOpen={!!viewingInvoicesForVendor}
            onClose={() => setViewingInvoicesForVendor(null)}
          />
        )}

      </div>
    </AppLayout>
  );
};

export default VendorManagement;